import { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Copy, ExternalLink, Trophy, ChevronUp, ChevronDown, Minus, Pencil, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCreateLeaderboard, useUpdateLeaderboard, useLeaderboard } from "@/hooks/use-leaderboard-mutations";
import { useAuth } from "@/contexts/auth-context";
import { useEntries, type StaffMemberMapped } from "@/hooks/use-entries";

const templates = [
  { id: "neon-pop", name: "Neon Pop" },
  { id: "clean-classic", name: "Clean Classic" },
  { id: "bold-sports", name: "Bold Sports" },
];

type EditorConfig = {
  name: string;
  startDate: string;
  endDate: string;
  selectedProducts: string[];
  metric: "memberships" | "value";
  topN: number;
  showNames: boolean;
  showAvatars: boolean;
  showMovement: boolean;
  refreshCadence: number;
  showCountdown: boolean;
  prize1: string;
  prize2: string;
  prize3: string;
  managerMsg: string;
  showHallOfFame: boolean;
  template: string;
};

const CampaignEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = id === "new" || !id;
  const templateState = (location.state as { template?: { config?: EditorConfig } } | null)?.template;

  const [config, setConfig] = useState<EditorConfig>(() => {
    if (templateState?.config) {
      return {
        ...templateState.config,
        startDate: "2026-03-01",
        endDate: "2026-03-31",
        template: "neon-pop",
      };
    }
    return {
      name: isNew ? "" : "March Membership Madness",
      startDate: "2026-03-01",
      endDate: "2026-03-31",
      selectedProducts: ["p1", "p2", "p3", "p4"],
      metric: "memberships" as "memberships" | "value",
      topN: 10,
      showNames: true,
      showAvatars: true,
      showMovement: true,
      refreshCadence: 10,
      showCountdown: true,
      prize1: "$1,000 Gift Card",
      prize2: "$500 Gift Card",
      prize3: "$250 Gift Card",
      managerMsg: "Great work team! Keep pushing for the top! 🚀",
      showHallOfFame: true,
      template: "neon-pop",
    };
  });

  const [previewTab, setPreviewTab] = useState<"tv" | "mobile">("tv");
  const [previewScreen, setPreviewScreen] = useState(0);
  const screenNames = ["Leaderboard", "Rewards", "Manager Message", "Hall of Fame"];

  const { effectiveVenueId } = useAuth();

  const { data: existingLeaderboard } = useLeaderboard(effectiveVenueId, isNew ? null : id);

  const createMutation = useCreateLeaderboard(effectiveVenueId);
  const updateMutation = useUpdateLeaderboard(effectiveVenueId, isNew ? null : id);
  const isSaving = createMutation.isPending || updateMutation.isPending;
  const saveError = createMutation.error || updateMutation.error;
  const [slugEditing, setSlugEditing] = useState(false);
  const [slugOverride, setSlugOverride] = useState<string | null>(null);

  const autoSlug = config.name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 60) || "new-campaign";

  const slug = slugOverride !== null ? slugOverride : autoSlug;
  const displayUrl = `${window.location.origin}/display/${isNew ? slug : (id ?? slug)}`;

  const { data: entries = [] } = useEntries(effectiveVenueId, isNew ? null : id);
  const leaderboardData = useMemo(() => {
    return entries.slice(0, config.topN);
  }, [entries, config.topN]);

  const handleSave = async () => {
    if (!config.name.trim()) return;

    const start = new Date(config.startDate);
    const end = new Date(config.endDate);
    const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    const period: "weekly" | "monthly" | "alltime" =
      diffDays <= 8 ? "weekly" : diffDays <= 32 ? "monthly" : "alltime";

    try {
      if (isNew) {
        const createPayload = {
          name: config.name,
          description: config.metric === "value" ? "$ value of memberships sold" : "Memberships sold",
          period,
          sortOrder: "desc" as const,
          maxEntries: config.topN,
          config: {
            prizes: [
              { place: 1, title: config.prize1, description: "" },
              { place: 2, title: config.prize2, description: "" },
              { place: 3, title: config.prize3, description: "" },
            ],
            managerMessage: { text: config.managerMsg, managerName: "Manager", title: "Manager" },
            products: config.selectedProducts,
          },
        };
        await createMutation.mutateAsync(createPayload);
      } else {
        const updatePayload = {
          name: config.name,
          description: config.metric === "value" ? "$ value of memberships sold" : "Memberships sold",
          sortOrder: "desc" as const,
          maxEntries: config.topN,
          config: {
            prizes: [
              { place: 1, title: config.prize1, description: "" },
              { place: 2, title: config.prize2, description: "" },
              { place: 3, title: config.prize3, description: "" },
            ],
            managerMessage: { text: config.managerMsg, managerName: "Manager", title: "Manager" },
            products: config.selectedProducts,
          },
        };
        await updateMutation.mutateAsync(updatePayload);
      }
      navigate("/app");
    } catch (_err) {
      // error is surfaced via saveError from mutation state
    }
  };

  useEffect(() => {
    if (existingLeaderboard && !isNew) {
      const rawBoard = existingLeaderboard as unknown as Record<string, unknown>;
      const cfg = rawBoard.config as Record<string, unknown> | undefined;
      const prizes = (cfg?.prizes as Array<{place: number; title: string; description: string}>) ?? [];
      const managerMsg = (cfg?.managerMessage as {text: string; managerName: string; title: string}) ?? null;
      const savedProducts = (cfg?.products as string[]) ?? [];
      setConfig((prev) => ({
        ...prev,
        name: existingLeaderboard.name,
        metric: existingLeaderboard.description?.includes("value") ? "value" : "memberships",
        topN: existingLeaderboard.maxEntries,
        prize1: prizes[0]?.title ?? prev.prize1,
        prize2: prizes[1]?.title ?? prev.prize2,
        prize3: prizes[2]?.title ?? prev.prize3,
        managerMsg: managerMsg?.text ?? prev.managerMsg,
        selectedProducts: savedProducts.length > 0 ? savedProducts : prev.selectedProducts,
      }));
    }
  }, [existingLeaderboard, isNew]);

  return (
    <div>
      <div className="mb-6 flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/app")}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Back
        </Button>
        <h1 className="text-xl font-bold">{isNew ? "Create Campaign" : "Edit Campaign"}</h1>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {/* Left: Settings */}
        <div className="space-y-6 overflow-auto">
          {/* Name & Dates */}
          <Section title="Campaign Details">
            <Field label="Campaign Name">
              <input
                className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
                value={config.name}
                onChange={(e) => setConfig({ ...config, name: e.target.value })}
                placeholder="e.g. March Membership Madness"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Start Date">
                <input type="date" className="w-full rounded-lg border bg-background px-3 py-2 text-sm" value={config.startDate} onChange={(e) => setConfig({ ...config, startDate: e.target.value })} />
              </Field>
              <Field label="End Date">
                <input type="date" className="w-full rounded-lg border bg-background px-3 py-2 text-sm" value={config.endDate} onChange={(e) => setConfig({ ...config, endDate: e.target.value })} />
              </Field>
            </div>
          </Section>

          {/* Products */}
          <Section title="Products">
            <div className="rounded-lg bg-primary/5 border border-primary/10 px-3 py-2 text-xs text-muted-foreground">
              Products will sync from your ROLLER account. Once synced, you can select which products count toward this leaderboard.
            </div>
          </Section>

          {/* Rules */}
          <Section title="Leaderboard Rules">
            <Field label="Primary Metric">
              <select className="w-full rounded-lg border bg-background px-3 py-2 text-sm" value={config.metric} onChange={(e) => setConfig({ ...config, metric: e.target.value as "memberships" | "value" })}>
                <option value="memberships"># of memberships sold</option>
                <option value="value">$ value of memberships sold</option>
              </select>
            </Field>
            <Field label="Top N">
              <input type="number" min={3} max={20} className="w-full rounded-lg border bg-background px-3 py-2 text-sm" value={config.topN} onChange={(e) => setConfig({ ...config, topN: Number(e.target.value) })} />
            </Field>
            <div className="flex flex-wrap gap-4">
              <Toggle label="Show Names" checked={config.showNames} onChange={(v) => setConfig({ ...config, showNames: v })} />
              <Toggle label="Show Avatars" checked={config.showAvatars} onChange={(v) => setConfig({ ...config, showAvatars: v })} />
              <Toggle label="Movement Indicators" checked={config.showMovement} onChange={(v) => setConfig({ ...config, showMovement: v })} />
              <Toggle label="Countdown Timer" checked={config.showCountdown} onChange={(v) => setConfig({ ...config, showCountdown: v })} />
            </div>
          </Section>

          {/* Rewards */}
          <Section title="Rewards">
            <Field label="1st Place"><input className="w-full rounded-lg border bg-background px-3 py-2 text-sm" value={config.prize1} onChange={(e) => setConfig({ ...config, prize1: e.target.value })} /></Field>
            <Field label="2nd Place"><input className="w-full rounded-lg border bg-background px-3 py-2 text-sm" value={config.prize2} onChange={(e) => setConfig({ ...config, prize2: e.target.value })} /></Field>
            <Field label="3rd Place"><input className="w-full rounded-lg border bg-background px-3 py-2 text-sm" value={config.prize3} onChange={(e) => setConfig({ ...config, prize3: e.target.value })} /></Field>
          </Section>

          {/* Manager Message */}
          <Section title="Manager Message">
            <textarea
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
              rows={3}
              value={config.managerMsg}
              onChange={(e) => setConfig({ ...config, managerMsg: e.target.value })}
            />
          </Section>

          {/* Theme */}
          <Section title="Theme">
            <div className="grid grid-cols-3 gap-3">
              {templates.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setConfig({ ...config, template: t.id })}
                  className={`rounded-lg border p-3 text-center text-sm transition-colors ${config.template === t.id ? "border-primary bg-primary/5 font-semibold" : "hover:bg-muted"}`}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </Section>

          {/* Deploy */}
          <Section title="Deploy">
            <div className="rounded-lg border bg-muted p-4 space-y-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Campaign URL Slug</p>
                <div className="mt-1.5 flex items-center gap-2">
                  {slugEditing ? (
                    <>
                      <input
                        data-testid="slug-input"
                        className="flex-1 rounded border bg-background px-2 py-1 text-xs font-mono"
                        value={slug}
                        onChange={(e) => setSlugOverride(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-"))}
                      />
                      <Button size="sm" variant="outline" onClick={() => setSlugEditing(false)} title="Confirm">
                        <Check className="h-3 w-3" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <code className="flex-1 truncate rounded bg-background px-2 py-1 text-xs font-mono">{slug}</code>
                      <Button size="sm" variant="outline" onClick={() => setSlugEditing(true)} title="Edit slug">
                        <Pencil className="h-3 w-3" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Display URL</p>
                <div className="mt-1.5 flex items-center gap-2">
                  <code className="flex-1 truncate rounded bg-background px-3 py-1.5 text-xs">{displayUrl}</code>
                  <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(displayUrl)}><Copy className="h-3 w-3" /></Button>
                  <Button size="sm" onClick={() => navigate(`/display/${isNew ? slug : (id ?? slug)}`)}><ExternalLink className="h-3 w-3" /></Button>
                </div>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {saveError && (
                <p className="text-sm text-destructive">
                  {saveError instanceof Error ? saveError.message : "Failed to save campaign"}
                </p>
              )}
              <Button
                className="w-full"
                onClick={handleSave}
                disabled={isSaving || !config.name.trim()}
              >
                {isSaving ? "Saving..." : isNew ? "Launch Campaign" : "Save Changes"}
              </Button>
            </div>
          </Section>
        </div>

        {/* Right: Live Preview */}
        <div className="sticky top-6">
          <div className="rounded-xl border bg-card shadow-lg overflow-hidden">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div className="flex gap-2">
                {(["tv", "mobile"] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => setPreviewTab(t)}
                    className={`rounded-md px-3 py-1 text-sm font-medium transition-colors ${previewTab === t ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                  >
                    {t === "tv" ? "TV Preview" : "Mobile Preview"}
                  </button>
                ))}
              </div>
              <div className="flex gap-1">
                {screenNames.map((name, i) => (
                  <button
                    key={i}
                    onClick={() => setPreviewScreen(i)}
                    className={`rounded px-2 py-0.5 text-xs transition-colors ${previewScreen === i ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
            <div className={`tv-gradient ${previewTab === "mobile" ? "mx-auto max-w-[375px]" : ""}`} style={{ minHeight: 400 }}>
              <AnimatePresence mode="wait">
                {previewScreen === 0 && (
                  <PreviewLeaderboard key="lb" data={leaderboardData} config={config} />
                )}
                {previewScreen === 1 && (
                  <PreviewRewards key="rw" config={config} />
                )}
                {previewScreen === 2 && (
                  <PreviewMessage key="msg" config={config} />
                )}
                {previewScreen === 3 && (
                  <PreviewHallOfFame key="hof" />
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PreviewLeaderboard = ({ data, config }: { data: StaffMemberMapped[]; config: { name: string; showCountdown: boolean; showAvatars: boolean; showNames: boolean; showMovement: boolean } }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="p-6">
    <div className="mb-4 flex items-center justify-between">
      <h3 className="text-lg font-bold text-tv-text">{config.name || "Campaign Name"}</h3>
      {config.showCountdown && <span className="text-sm text-neon-cyan animate-pulse-neon">16d 8h remaining</span>}
    </div>
    <div className="space-y-2">
      {data.map((s) => (
        <div key={s.id} className="flex items-center gap-3 rounded-lg bg-tv-surface px-4 py-2.5">
          <span className={`w-8 text-center text-lg font-bold ${s.rank <= 3 ? "text-neon-orange" : "text-tv-muted"}`}>
            {s.rank}
          </span>
          {config.showAvatars && (
            <img src={s.avatar} alt={s.name} className="h-8 w-8 rounded-full object-cover" />
          )}
          <span className="flex-1 text-sm font-medium text-tv-text">{config.showNames ? s.name : `Staff ${s.rank}`}</span>
          <span className="text-sm font-bold text-neon-cyan">{s.membershipsSold}</span>
          <span className="text-xs text-tv-muted">${s.valueSold.toLocaleString()}</span>
          {config.showMovement && (
            <span className="w-6 text-center">
              {s.movement > 0 ? <ChevronUp className="h-4 w-4 text-neon-green" /> : s.movement < 0 ? <ChevronDown className="h-4 w-4 text-neon-orange" /> : <Minus className="h-3 w-3 text-tv-muted" />}
            </span>
          )}
        </div>
      ))}
    </div>
  </motion.div>
);

const PreviewRewards = ({ config }: { config: { prize1: string; prize2: string; prize3: string } }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center p-8">
    <Trophy className="mb-4 h-12 w-12 text-neon-orange" />
    <h3 className="text-xl font-bold text-tv-text neon-glow-orange">What's Up For Grabs</h3>
    <div className="mt-6 w-full space-y-4">
      {[config.prize1, config.prize2, config.prize3].map((p: string, i: number) => (
        <div key={i} className="flex items-center gap-4 rounded-lg bg-tv-surface px-5 py-3">
          <span className="text-2xl font-bold text-neon-orange">{["🥇", "🥈", "🥉"][i]}</span>
          <span className="text-lg font-semibold text-tv-text">{p || `Prize ${i + 1}`}</span>
        </div>
      ))}
    </div>
  </motion.div>
);

const PreviewMessage = ({ config }: { config: { managerMsg: string } }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center p-8 text-center" style={{ minHeight: 350 }}>
    <p className="max-w-sm text-xl font-medium leading-relaxed text-tv-text">
      "{config.managerMsg || "Your message here…"}"
    </p>
    <p className="mt-6 text-sm text-tv-muted">— Your Manager</p>
  </motion.div>
);

const PreviewHallOfFame = () => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="p-6">
    <h3 className="mb-4 text-center text-xl font-bold text-tv-text neon-glow-cyan">Hall of Fame</h3>
    <p className="mb-6 text-center text-sm text-tv-muted">Top performers will be celebrated here.</p>
    <div className="grid grid-cols-3 gap-3">
      {["🥇", "🥈", "🥉"].map((medal, i) => (
        <div key={i} className="rounded-lg bg-tv-surface p-3 text-center">
          <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-tv-surface/80 text-xl">{medal}</div>
          <p className="text-xs font-semibold text-tv-text">Top Performer</p>
          <p className="text-[10px] text-tv-muted">This month</p>
        </div>
      ))}
    </div>
  </motion.div>
);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="rounded-xl border bg-card p-5">
    <h3 className="mb-4 font-semibold">{title}</h3>
    <div className="space-y-3">{children}</div>
  </div>
);

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);

const Toggle = ({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) => (
  <label className="flex items-center gap-2 text-sm cursor-pointer">
    <div
      className={`relative h-5 w-9 rounded-full transition-colors ${checked ? "bg-primary" : "bg-muted"}`}
      onClick={() => onChange(!checked)}
    >
      <div className={`absolute top-0.5 h-4 w-4 rounded-full bg-background shadow transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
    </div>
    {label}
  </label>
);

export default CampaignEditor;
